<template>
    <form @submit="onSubmit" class="relative">
        <Link2Icon class="text-muted-foreground absolute left-3 top-1/2 z-10 -translate-y-1/2">
        </Link2Icon>

        <TextControlField
            name="destinationUrl"
            placeholder="Paste your link here to shorten it"
            input-class="text-base h-14 ps-12 pe-40"
            class="[&_.error-message]:bg-destructive [&_.error-message]:text-destructive-foreground [&_.error-message]:absolute [&_.error-message]:end-24 [&_.error-message]:top-1/2 [&_.error-message]:-translate-y-1/2 [&_.error-message]:rounded [&_.error-message]:px-2 [&_.error-message]:py-1 [&_.error-message]:text-xs" />

        <div class="absolute end-1 top-1/2 flex h-12 -translate-y-1/2 font-semibold">
            <Button
                :disabled="
                    isSubmitting ||
                    values.destinationUrl ===
                        results?.createdShortUrlRecord?.publicShortUrl.destinationUrl
                "
                class="h-full">
                <span :class="[isSubmitting && 'opacity-0']">Shorten</span>
            </Button>

            <div
                :class="[
                    'pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                    isSubmitting ? 'opacity-100' : 'opacity-0',
                ]">
                <Spinner class="text-primary-foreground"></Spinner>
            </div>
        </div>
    </form>
    <FadeTransition>
        <p v-if="errorMessage" class="bg-yellow mx-2 rounded-b px-3 py-1 text-xs">
            {{ errorMessage }}
        </p>
    </FadeTransition>

    <FadeTransition>
        <!-- New URL that has not been shortened before -->
        <div
            v-if="results?.createdShortUrlRecord"
            class="border-primary bg-background mt-4 flex items-center gap-1 rounded-md border-2 p-3 shadow-lg">
            <span class="text-muted-foreground flex-shrink truncate">
                {{ results?.createdShortUrlRecord.publicShortUrl.destinationUrl }}
            </span>

            <span class="flex-grow"></span>

            <a
                :href="results.createdShortUrlRecord.absolutePath"
                target="_blank"
                class="text-primary flex-shrink-0 p-1">
                {{ results.createdShortUrlRecord.absolutePath }}
            </a>

            <UrlQrDialog :short-url="results.createdShortUrlRecord.absolutePath"> </UrlQrDialog>

            <CopyButton
                :text-to-copy="results.createdShortUrlRecord.absolutePath"
                variant="ghost"
                class="h-auto w-auto p-2" />
        </div>
        <!-- URL that has been shortened before -->
        <div
            v-else-if="
                results?.existingPublicShortUrl || results?.existingOfficialShortUrls?.length
            "
            class="border-muted bg-background mt-4 rounded-md border-2 p-3 shadow-md">
            <!-- User's existing shortened URL -->
            <div v-if="results?.existingPublicShortUrl">
                <p class="mb-1 text-sm font-medium">This URL has already been shortened:</p>

                <div class="bg-muted/30 flex items-center gap-1 rounded-md border p-2">
                    <span class="text-muted-foreground flex-shrink truncate text-sm">
                        {{ previousDestUrl }}
                    </span>

                    <span class="flex-grow"></span>

                    <a
                        :href="results.existingPublicShortUrl"
                        target="_blank"
                        class="text-primary flex-shrink-0 p-1 font-medium">
                        {{ results.existingPublicShortUrl }}
                    </a>

                    <UrlQrDialog :short-url="results.existingPublicShortUrl"></UrlQrDialog>

                    <CopyButton
                        :text-to-copy="results.existingPublicShortUrl"
                        variant="ghost"
                        class="h-auto w-auto p-2"
                        icon-only />
                </div>
            </div>

            <!-- Official government short URLs -->
            <div v-if="results?.existingOfficialShortUrls?.length" class="mt-3">
                <p class="text-sm font-medium">
                    {{
                        results.existingOfficialShortUrls.length > 1
                            ? 'Official government versions also available:'
                            : 'Official government version also available:'
                    }}
                </p>

                <div class="mt-1 space-y-1">
                    <div
                        v-for="(url, index) in results.existingOfficialShortUrls"
                        :key="index"
                        class="flex items-center gap-3 rounded-md border p-2">
                        <ShieldCheckIcon class="text-muted-foreground/70 size-4" />

                        <a
                            :href="url"
                            target="_blank"
                            class="text-primary flex-grow truncate text-sm">
                            {{ url }}
                        </a>

                        <CopyButton
                            :text-to-copy="url"
                            size="sm"
                            variant="ghost"
                            icon-only
                            class="text-muted-foreground h-6 w-6 p-0.5" />
                    </div>
                </div>
            </div>
        </div>
    </FadeTransition>

    <p class="text-primary-foreground mt-4 text-center text-sm">
        go.gov.bn is a URL shortener for Official Brunei Government websites only.
    </p>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { Link2Icon, ShieldCheckIcon } from 'lucide-vue-next'
import UrlQrDialog from './UrlQrDialog.vue'
import { useForm } from 'vee-validate'
import { ref } from 'vue'
import { publicShortUrlsApi } from '@/website/api/publicShortUrls'
import { TextControlField } from '@/website/components/core/forms/text-control'
import { object, string } from 'yup'
import { FadeTransition } from '@/components/core/transitions'
import { Spinner } from '@/components/core/spinners'
import CopyButton from '@/website/components/core/buttons/CopyButton.vue'
import type { CreatedPublicShortUrlRecord } from '@/website/types'

const results = ref<CreatedPublicShortUrlRecord>()
const errorMessage = ref('')
const previousDestUrl = ref('')

const { handleSubmit, isSubmitting, values } = useForm<{ destinationUrl: string }>({
    validationSchema: object({
        destinationUrl: string().trim().url().label('URL'),
    }),
})

const onSubmit = handleSubmit(async (values) => {
    if (!values.destinationUrl?.trim()) return

    previousDestUrl.value = values.destinationUrl
    results.value = undefined
    errorMessage.value = ''
    const [apiResults, apiErrorMessage] = await publicShortUrlsApi.create(values.destinationUrl)
    results.value = apiResults
    errorMessage.value = apiErrorMessage
})
</script>

<style scoped></style>
